import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { rememberDestination } from "@/features/auth/afterSignIn";
import { hasSession } from "@/lib/session";

/**
 * The state a link inside the app puts on a public page: where it was
 * pressed. Absent when the page was opened from outside — a chat, a QR
 * code, a new tab.
 */
export function fromInsideTheApp(pathname: string): { from: string } {
  return { from: pathname };
}

export function cameFrom(state: unknown): string | null {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/") ? from : null;
}

/**
 * Where a reader goes next from somebody's page or a shared album.
 *
 * The button under both said "Start your own timelapse" and led to the
 * door. Right for a stranger; said to somebody signed in, it invited them
 * to start what they already have, and the door only turned them round to
 * the diary. The phone's card over the feed is as easy to leave as it was
 * to arrive; the web page had no way out but that button.
 *
 * Signed in, the button gives way to the way back: to the page inside the
 * app the link was pressed on, when it was — the feed, the directory, a
 * follow list, the profile's own address — and to the diary otherwise,
 * for a link opened from outside. Signed out, and on the server, the
 * stranger's button: the token lives in localStorage, which the server
 * cannot read, so the first client render agrees with the HTML that
 * arrived and the swap comes after — the same rule as VisitorView.
 */
export function VisitorExit() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => setSignedIn(hasSession()), []);

  if (!signedIn) {
    return (
      <Link
        to="/"
        // This page again once the sign-in is through — with Follow on it,
        // which is what a friend's link was for. See `afterSignIn`.
        onClick={() => rememberDestination(location.pathname)}
        className="self-start rounded-cta bg-accent px-5 py-3 font-medium text-cta-ink text-sm"
      >
        {t("public.cta")}
      </Link>
    );
  }
  if (cameFrom(location.state)) {
    // Back through the history rather than a link to the path: a link
    // would add the feed on top of the feed, and Back after that would
    // return to this page.
    return (
      <button
        type="button"
        onClick={() => void navigate(-1)}
        className="self-start rounded-cta border border-hairline px-5 py-3 text-sm"
      >
        {t("public.back")}
      </button>
    );
  }
  return (
    <Link
      to="/dashboard"
      className="self-start rounded-cta border border-hairline px-5 py-3 text-sm"
    >
      {t("settings.back")}
    </Link>
  );
}
